import { MouseEventHandler, ReactElement } from 'react'
import { ButtonBase, styled } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { Typography } from '../../text'
import { IconContext } from '@phosphor-icons/react'
import { Link as RouterLink } from 'react-router-dom'

type TextButtonVariants = 'primary' | 'destructive' | 'secondary'

type TextButtonContentProps = {
    label: string
    leftIcon?: ReactElement
    rightIcon?: ReactElement
    variant?: TextButtonVariants
    disabled?: boolean
}

type TextButtonInteractionProps =
    | {
          onClick: MouseEventHandler<HTMLButtonElement>
          type?: 'button'
      }
    | {
          onClick?: MouseEventHandler<HTMLButtonElement>
          type: 'submit'
      }

export type TextButtonProps = TextButtonInteractionProps & {
    fullWidth?: boolean
} & TextButtonContentProps

export type TextNavButtonProps = {
    href: string
} & TextButtonContentProps

function getLeftIconColor(disabled: boolean | undefined, variant: TextButtonVariants) {
    if (disabled) {
        return tokens.ColorIconDisabled
    }
    if (variant === 'secondary') {
        return tokens.ColorIconBoldest
    }
    return tokens.ColorIconInverse
}

function getRightIconColor(disabled: boolean | undefined, variant: TextButtonVariants) {
    if (disabled) {
        return tokens.ColorIconDisabled
    }
    if (variant === 'secondary') {
        return tokens.ColorIconSubtle
    }
    return tokens.ColorIconInverse
}

function TextButtonContent({
    label,
    leftIcon,
    rightIcon,
    disabled,
    variant,
}: {
    label: string
    leftIcon?: ReactElement
    rightIcon?: ReactElement
    disabled?: boolean
    variant: TextButtonVariants
}) {
    return (
        <>
            {leftIcon && (
                <IconContext.Provider
                    value={{
                        size: tokens.SizingIconS,
                        weight: 'bold',
                        color: getLeftIconColor(disabled, variant),
                    }}
                >
                    {leftIcon}
                </IconContext.Provider>
            )}
            <Typography variant="link" noWrap>
                {label}
            </Typography>
            {rightIcon && (
                <IconContext.Provider
                    value={{
                        size: tokens.SizingIconS,
                        weight: 'bold',
                        color: getRightIconColor(disabled, variant),
                    }}
                >
                    {rightIcon}
                </IconContext.Provider>
            )}
        </>
    )
}

export function TextButtonNew({
    label,
    leftIcon,
    rightIcon,
    variant = 'primary',
    onClick,
    disabled,
    fullWidth,
    type = 'button',
}: TextButtonProps) {
    return (
        <StyledButtonBase
            type={type}
            onClick={onClick}
            disableRipple
            disabled={disabled}
            sx={{ ...getButtonVariantStyles(variant), ...(fullWidth && { maxWidth: 'none', width: '100%' }) }}
        >
            <TextButtonContent label={label} leftIcon={leftIcon} rightIcon={rightIcon} disabled={disabled} variant={variant} />
        </StyledButtonBase>
    )
}

export function TextNavButtonNew({ label, leftIcon, rightIcon, variant = 'primary', href, disabled }: TextNavButtonProps) {
    return (
        <StyledButtonBase disableRipple disabled={disabled} component={RouterLink} to={href} sx={getButtonVariantStyles(variant)}>
            <TextButtonContent label={label} leftIcon={leftIcon} rightIcon={rightIcon} disabled={disabled} variant={variant} />
        </StyledButtonBase>
    )
}

const StyledButtonBase = styled(ButtonBase)(() => ({
    lineHeight: 0,
    width: 'fit-content',
    maxWidth: `calc(${tokens.SizingSpacing400} * 9)`,
    height: tokens.SizingSpacing400,
    gap: tokens.SizingSpacing100,
    paddingTop: tokens.SizingSpacingNone,
    paddingRight: tokens.SizingSpacing100,
    paddingBottom: tokens.SizingSpacingNone,
    paddingLeft: tokens.SizingSpacing100,
    borderRadius: tokens.SizingCornerS,
    '&.Mui-disabled': {
        color: tokens.ColorTextDisabled,
        backgroundColor: tokens.ColorSurfaceDisabled,
        boxShadow: 'none',
    },
})) as typeof ButtonBase

function getButtonVariantStyles(variant: TextButtonVariants) {
    return {
        ...TEXT_BUTTON_VARIANTS[variant].default,
        '&:hover': {
            ...TEXT_BUTTON_VARIANTS[variant].hover,
        },
        '&:active': {
            ...TEXT_BUTTON_VARIANTS[variant].pressed,
        },
    }
}

const TEXT_BUTTON_VARIANTS = {
    primary: {
        default: {
            backgroundColor: tokens.ColorSurfacePositiveBoldDefault,
            color: tokens.ColorTextInverse,
        },
        hover: { backgroundColor: tokens.ColorSurfacePositiveBoldHover, color: tokens.ColorTextInverse },
        pressed: { backgroundColor: tokens.ColorSurfacePositiveBoldPressed, color: tokens.ColorTextInverse },
    },
    destructive: {
        default: {
            backgroundColor: tokens.ColorSurfaceDangerBoldDefault,
            color: tokens.ColorTextInverse,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceDangerBoldHover,
            color: tokens.ColorTextInverse,
        },
        pressed: {
            backgroundColor: tokens.ColorSurfaceDangerBoldPressed,
            color: tokens.ColorTextInverse,
        },
    },
    secondary: {
        default: {
            backgroundColor: tokens.ColorSurfaceDefault,
            color: tokens.ColorTextDefault,
            border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
        },
        hover: {
            backgroundColor: tokens.ColorSurfaceHover,
            color: tokens.ColorTextDefault,
            border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
        },
        pressed: {
            backgroundColor: tokens.ColorSurfacePressed,
            color: tokens.ColorTextDefault,
            border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
        },
    },
}
